import { useState } from 'react'

import {
  useProductUnderstandingDetailApi,
  useProductUnderstandingListApi
} from '@/common/api/modules/productUnderstanding'
import { useSiteId } from '@/common/app/site'
import { dateTimeStr } from '@/common/form/date'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'

// 状態バッジ（success/fallback/pending/failed）
const STATUS_META: Record<string, { label: string; bg: string; color: string }> = {
  success: { label: '● 理解済み', bg: '#E5F4ED', color: '#1A9E6B' },
  fallback: { label: '◐ 簡易テキスト', bg: '#FBF1DE', color: '#D08A1A' },
  pending: { label: '○ 処理待ち', bg: '#EEF1F4', color: '#5A636E' },
  failed: { label: '× 失敗', bg: '#FBE9E9', color: '#D24B4B' }
}
const StatusBadge = ({ s }: { s: string }) => {
  const m = STATUS_META[s] ?? { label: s, bg: '#EEF1F4', color: '#5A636E' }
  return (
    <span className='inline-block rounded-md px-2 py-0.5 text-[11px] font-bold' style={{ background: m.bg, color: m.color }}>
      {m.label}
    </span>
  )
}

// 8軸ラベル（表示順・ラベル）
const AXES: { key: keyof PuAttr; label: string; list?: boolean }[] = [
  { key: 'material', label: '素材' },
  { key: 'size_fit', label: 'サイズ・寸法' },
  { key: 'features', label: '特徴', list: true },
  { key: 'use_scenes', label: '用途・シーン', list: true },
  { key: 'target', label: '対象' },
  { key: 'color_pattern', label: '色・柄' },
  { key: 'care_spec', label: 'ケア・仕様' },
  { key: 'appeal', label: '訴求' }
]
interface PuAttr {
  material: string | null
  size_fit: string | null
  features: string[]
  use_scenes: string[]
  target: string | null
  color_pattern: string | null
  care_spec: string | null
  appeal: string | null
}

const th = 'border-primary border px-3 py-2 text-left align-middle text-xs font-normal whitespace-nowrap'
const td = 'border-primary border px-3 py-2 align-middle'

const STATUS_FILTERS = [
  { value: '', label: 'すべて' },
  { value: 'success', label: '理解済み' },
  { value: 'fallback', label: '簡易テキスト' },
  { value: 'pending', label: '処理待ち' },
  { value: 'failed', label: '失敗' }
]

const Kpi = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className='border-primary flex-1 rounded-xl border px-4 py-3'>
    <div className='text-tertiary text-[11px]'>{label}</div>
    <div className='text-emphasis mt-1 text-[22px] font-bold tabular-nums'>{value}</div>
    {sub && <div className='text-tertiary text-[11px]'>{sub}</div>}
  </div>
)

const ProductUnderstandingListSection = () => {
  const siteId = useSiteId()
  const [q, setQ] = useState('')
  const [qInput, setQInput] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const perPage = 30
  const { data, isLoading } = useProductUnderstandingListApi({
    site_id: siteId,
    page,
    per_page: perPage,
    ...(q && { q }),
    ...(status && { status })
  })
  const [selected, setSelected] = useState<string | null>(null)
  const { data: detail, isLoading: detailLoading } = useProductUnderstandingDetailApi(
    selected ? { site_id: siteId, product_id: selected } : undefined
  )

  const kpi = data?.kpi
  const rows = data?.items ?? []
  const totalPages = data ? Math.max(1, Math.ceil(data.total / perPage)) : 1

  const attr = (detail?.attributes ?? null) as PuAttr | null

  return (
    <>
      <Text size={12} color={TextColor.SECONDARY}>
        商品情報からAIが抽出した構造化属性（8軸）の理解状況です。行をクリックすると理解内容（品質表示ラベル）を確認できます。再抽出・品質管理は Phase 2 で追加予定。
      </Text>

      {/* KPI */}
      <div className='mt-3 flex flex-wrap gap-3'>
        <Kpi label='対象商品数' value={kpi ? kpi.total.toLocaleString() : '—'} />
        <Kpi label='理解済みカバレッジ' value={kpi ? `${kpi.coverage_pct}%` : '—'} sub={kpi ? `${kpi.success.toLocaleString()}件` : ''} />
        <Kpi label='簡易テキスト率' value={kpi ? `${kpi.fallback_pct}%` : '—'} sub={kpi ? `うち薄い商品 ${kpi.skipped.toLocaleString()}件` : ''} />
        <Kpi label='最終更新' value={kpi?.last_updated ? dateTimeStr(kpi.last_updated, { omitSecond: true }) : '—'} />
      </div>

      {/* toolbar */}
      <div className='mt-4 flex flex-wrap items-center gap-3'>
        <input
          className='border-border-input rounded-md border px-3 py-1.5 text-sm'
          placeholder='商品名で検索'
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(1)
              setQ(qInput.trim())
            }
          }}
        />
        <select
          className='border-border-input rounded-md border px-3 py-1.5 text-sm'
          value={status}
          onChange={(e) => {
            setPage(1)
            setStatus(e.target.value)
          }}
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <Text size={12} color={TextColor.SECONDARY}>
          {data ? `${data.total.toLocaleString()}件` : ''}
        </Text>
      </div>

      <Area>
        {isLoading ? (
          <PageListSearching />
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full border-collapse text-sm'>
              <thead>
                <tr className='bg-fill-surface-tertiary'>
                  <th className={th}>商品名</th>
                  <th className={th}>状態</th>
                  <th className={th}>抽出軸数</th>
                  <th className={th}>特徴（抜粋）</th>
                  <th className={th}>抽出日時</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.product_id}
                    className='hover:bg-surface-secondary cursor-pointer'
                    onClick={() => setSelected(r.product_id)}
                  >
                    <td className={td}>
                      <span className='text-[12.5px] font-semibold'>{r.name}</span>
                      <br />
                      <code className='text-tertiary font-mono text-[10.5px]'>{r.product_id}</code>
                    </td>
                    <td className={td}>
                      <StatusBadge s={r.extraction_status} />
                    </td>
                    <td className={`${td} tabular-nums`}>{r.axes_count} / 8</td>
                    <td className={td}>
                      <div className='flex flex-wrap gap-1'>
                        {r.features.map((f) => (
                          <span key={f} className='bg-surface-secondary text-secondary rounded px-1.5 py-0.5 text-[10.5px]'>
                            {f}
                          </span>
                        ))}
                        {r.features.length === 0 && <span className='text-tertiary text-[11px]'>—</span>}
                      </div>
                    </td>
                    <td className={`${td} text-secondary whitespace-nowrap font-mono text-[11px]`}>
                      {r.updated_at ? dateTimeStr(r.updated_at, { omitSecond: true }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && (
              <div className='py-10 text-center'>
                <Text color={TextColor.SECONDARY}>該当する商品はありません</Text>
              </div>
            )}
          </div>
        )}

        {/* ページング */}
        {data && data.total > perPage && (
          <div className='mt-3 flex items-center justify-center gap-3'>
            <Button color={ButtonColor.SECONDARY} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              前へ
            </Button>
            <Text size={12} color={TextColor.SECONDARY}>
              {page} / {totalPages}
            </Text>
            <Button color={ButtonColor.SECONDARY} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              次へ
            </Button>
          </div>
        )}
      </Area>

      <Text size={12} color={TextColor.SECONDARY}>
        行をクリックすると、AIの理解内容（品質表示ラベル）を確認できます。「記載なし」は原文に情報が無いため未記入であることを示します（AIは原文に無い情報を創作しません）。
      </Text>

      {/* 詳細モーダル（商品理解ラベル） */}
      {selected && (
        <div className='fixed inset-0 z-modal flex items-center justify-center bg-black/60 p-4' onClick={() => setSelected(null)}>
          <div
            className='border-secondary bg-surface shadow-area flex max-h-[88vh] w-full max-w-3xl flex-col rounded-xl border'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='border-secondary flex items-center justify-between border-b px-5 py-3'>
              <Text size={16} weight='bold'>
                商品理解ラベル
              </Text>
              <button type='button' className='text-secondary hover:text-primary text-xl leading-none' onClick={() => setSelected(null)} aria-label='閉じる'>
                ×
              </button>
            </div>

            <div className='grid gap-4 overflow-auto px-5 py-4 md:grid-cols-[1fr_260px]'>
              {detailLoading || !detail ? (
                <PageListSearching />
              ) : (
                <>
                  <div>
                    <Text size={14} weight='bold'>
                      {detail.name}
                    </Text>
                    <div className='text-tertiary mt-0.5 font-mono text-[11px]'>{detail.product_id}</div>

                    {attr ? (
                      <div className='border-primary mt-3 overflow-hidden rounded-lg border'>
                        {AXES.map((ax, i) => {
                          const v = attr[ax.key]
                          const empty = ax.list ? (v as string[]).length === 0 : !v
                          return (
                            <div key={ax.key} className={`grid grid-cols-[110px_1fr] gap-3 px-3 py-2 text-[12.5px] ${i > 0 ? 'border-primary border-t' : ''}`}>
                              <span className='text-secondary'>{ax.label}</span>
                              <span>
                                {ax.list ? (
                                  (v as string[]).length > 0 ? (
                                    <span className='flex flex-wrap gap-1'>
                                      {(v as string[]).map((x) => (
                                        <span key={x} className='bg-surface-secondary rounded px-1.5 py-0.5 text-[11px]'>
                                          {x}
                                        </span>
                                      ))}
                                    </span>
                                  ) : (
                                    <span className='text-tertiary italic'>記載なし</span>
                                  )
                                ) : empty ? (
                                  <span className='text-tertiary italic'>
                                    {ax.key === 'care_spec' ? '記載なし ※原文に情報が無いため未記入' : '記載なし'}
                                  </span>
                                ) : (
                                  <span>{v as string}</span>
                                )}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <div className='bg-surface-secondary mt-3 rounded-lg px-4 py-6 text-center'>
                        <Text weight='bold'>
                          {detail.extraction_status === 'pending' ? '処理待ちです' : '簡易テキストで代替中です'}
                        </Text>
                        <div className='text-secondary mt-1 text-[12px]'>
                          {detail.extraction_status === 'pending'
                            ? '次回の同期バッチでAI抽出が実行されます。'
                            : 'AI抽出の対象外（情報が少ない商品）のため、整形済みの元テキストを検索に使用しています。商品説明を充実させると理解精度が向上します。'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 処理情報サイド */}
                  <div className='flex flex-col gap-3'>
                    <div className='border-primary rounded-lg border px-3 py-3 text-[12px]'>
                      <div className='text-secondary mb-2 text-[11px] font-bold'>処理情報</div>
                      <div className='flex items-center justify-between py-0.5'>
                        <span className='text-tertiary'>状態</span>
                        <StatusBadge s={detail.extraction_status} />
                      </div>
                      <div className='flex items-center justify-between py-0.5'>
                        <span className='text-tertiary'>モデル</span>
                        <span className='font-mono text-[11px]'>{detail.model_name ?? '—'}</span>
                      </div>
                      <div className='flex items-center justify-between py-0.5'>
                        <span className='text-tertiary'>トークン</span>
                        <span className='font-mono text-[11px]'>
                          {(detail.prompt_tokens ?? 0) + '/' + (detail.completion_tokens ?? 0)}
                        </span>
                      </div>
                      <div className='flex items-center justify-between py-0.5'>
                        <span className='text-tertiary'>スキーマ版</span>
                        <span className='font-mono text-[11px]'>v{detail.schema_version}</span>
                      </div>
                      <div className='flex items-center justify-between py-0.5'>
                        <span className='text-tertiary'>ベクトル反映</span>
                        <span>{detail.embedding_reflected ? '✓ 反映済' : '—'}</span>
                      </div>
                    </div>
                    <div className='border-primary rounded-lg border px-3 py-3'>
                      <div className='text-secondary mb-1 text-[11px] font-bold'>検索に使う理解テキスト</div>
                      <div className='text-tertiary whitespace-pre-wrap break-all font-mono text-[10.5px]'>
                        {detail.source_excerpt || '—'}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className='border-secondary flex justify-end border-t px-5 py-3'>
              <Button color={ButtonColor.SECONDARY} onClick={() => setSelected(null)}>
                閉じる
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ProductUnderstandingListSection
